import { ColorOption, VariantOption, ConfiguratorModule, ConfiguratorType } from '../types';
import { getState, setState, subscribe } from '../state';
import { applyColorToModel } from '../utils/modelHelper';

// ─── Fallback data ────────────────────────────────────────────────────────────

const DEFAULT_COLOR_OPTIONS: ColorOption[] = [
  { label: 'Midnight',   hex: '#1c2b4a' },
  { label: 'Cognac',     hex: '#7a3b1e' },
  { label: 'Camel',      hex: '#c8853a' },
  { label: 'Ivory',      hex: '#f0ece4' },
];

const DEFAULT_VARIANT_OPTIONS: VariantOption[] = [
  { id: 'variant-a', label: 'Standard' },
  { id: 'variant-b', label: 'Premium'  },
];

// ─── Icons ───────────────────────────────────────────────────────────────────

const ICO_EXPAND     = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
const ICO_COLLAPSE   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;
/** Returns the CSS background value for a 3D-globe swatch at `hex` (or a neutral checkerboard for '').  */
function globeBackground(hex: string): string {
  return hex
    ? `radial-gradient(circle at 36% 30%, rgba(255,255,255,0.62) 0%, transparent 52%),
       radial-gradient(circle at 65% 72%, rgba(0,0,0,0.32) 0%, transparent 48%),
       ${hex}`
    : `conic-gradient(#888 0deg 90deg, #aaa 90deg 180deg, #888 180deg 270deg, #aaa 270deg)`;
}
const ICO_VARIANTS   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>`;
// Variant tier icons (index 0 = first option, 1 = second, etc.)
const VARIANT_ICONS = [
  `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="5"/><circle cx="8" cy="8" r="2"/></svg>`,
  `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2l1.8 3.6L14 6.5l-3 2.9.7 4.1L8 11.4l-3.7 2.1.7-4.1-3-2.9 4.2-.9z"/></svg>`,
  `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2l2 4h4l-3 2.5 1 4L8 10l-4 2.5 1-4L2 6h4z"/></svg>`,
];

const ICO_RESET      = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38"/></svg>`;
const ICO_FULLSCREEN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
const ICO_SHARE      = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`;
const ICO_INFO       = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

// ─── Builder ─────────────────────────────────────────────────────────────────

type FlyoutSection = 'colour' | 'variants';

export function createRightPanel(
  container: HTMLElement,
  configurators: Record<ConfiguratorType, ConfiguratorModule>,
  onVariantModelSwap?: () => void
): HTMLElement {

  // Wrap: flex row-reverse so flyout (left) and panel (right) sit side-by-side
  const wrap = document.createElement('div');
  wrap.className = 'cfg-panel-wrap';

  // ── Flyout (single-section popout, shown while panel is collapsed) ────────
  const flyout = document.createElement('div');
  flyout.className = 'cfg-flyout';
  flyout.style.display = 'none';
  wrap.appendChild(flyout);

  // ── Single panel ──────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  panel.className = 'cfg-right-panel';

  // Toggle button — always at the top
  const toggleBtn = document.createElement('button');
  toggleBtn.className = 'cfg-panel-toggle-btn';
  toggleBtn.setAttribute('aria-label', 'Expand panel');

  // Strip content (collapsed view)
  const stripView = document.createElement('div');
  stripView.className = 'cfg-strip-view';

  // Full content (expanded view)
  const fullView = document.createElement('div');
  fullView.className = 'cfg-full-view';
  fullView.style.display = 'none';

  panel.appendChild(toggleBtn);
  panel.appendChild(stripView);
  panel.appendChild(fullView);
  wrap.appendChild(panel);
  container.appendChild(wrap);

  // ── State ─────────────────────────────────────────────────────────────────
  let expanded      = false;
  let activeFlyout:  FlyoutSection | null = null;
  let pinnedSection: FlyoutSection | null = null;   // set by click, cleared by re-click
  let pinnedAnchor:  HTMLElement   | null = null;

  const syncPinnedHighlight = (): void => {
    stripView.querySelectorAll<HTMLElement>('[data-section]').forEach((b) => {
      b.classList.toggle('cfg-strip-btn--pinned', b.dataset.section === pinnedSection);
    });
  };

  const isMobile = (): boolean => window.matchMedia('(max-width: 768px)').matches;

  const syncStripH = (): void => {
    if (isMobile()) {
      container.style.setProperty('--strip-h', `${wrap.getBoundingClientRect().height}px`);
    } else {
      container.style.removeProperty('--strip-h');
    }
  };

  const setExpanded = (val: boolean): void => {
    expanded = val;
    panel.classList.toggle('cfg-right-panel--expanded', expanded);
    toggleBtn.innerHTML = expanded
      ? `${ICO_COLLAPSE}<span class="cfg-strip-btn__label">Close</span>`
      : `${ICO_EXPAND}<span class="cfg-strip-btn__label">Expand</span>`;
    toggleBtn.setAttribute('aria-label', expanded ? 'Collapse panel' : 'Expand panel');
    if (isMobile()) {
      // On mobile: strip always visible; full-view slides above when expanded
      stripView.style.display = 'flex';
      fullView.style.display  = expanded ? 'flex' : 'none';
    } else {
      stripView.style.display = expanded ? 'none' : 'flex';
      fullView.style.display  = expanded ? 'flex' : 'none';
    }
    if (expanded) { pinnedSection = null; pinnedAnchor = null; syncPinnedHighlight(); setFlyout(null); }
    requestAnimationFrame(syncStripH);
  };

  const setFlyout = (section: FlyoutSection | null, anchorEl?: HTMLElement): void => {
    activeFlyout = section;
    // Hover highlight (active = currently visible, pinned = pinned-open)
    stripView.querySelectorAll<HTMLElement>('[data-section]').forEach((b) => {
      b.classList.toggle('cfg-strip-btn--active', b.dataset.section === section);
    });
    if (!section) {
      flyout.style.display = 'none';
      return;
    }
    // Render first so flyout has measurable height, then center on anchor button
    flyout.style.display = 'flex';
    renderFlyout(section);
    if (anchorEl) {
      const panelTop  = panel.getBoundingClientRect().top;
      const btnRect   = anchorEl.getBoundingClientRect();
      const btnCenter = btnRect.top + btnRect.height / 2 - panelTop;
      const mt        = Math.max(0, btnCenter - flyout.offsetHeight / 2);
      flyout.style.marginTop = `${mt}px`;
    }
  };

  // ── Hover-flyout timer ────────────────────────────────────────────────────
  // Small delay so the mouse can travel from icon → flyout without it closing.
  // On close, revert to pinned section (if any) instead of always hiding.
  let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelHoverClose = (): void => {
    if (hoverCloseTimer !== null) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null; }
  };

  const scheduleHoverClose = (): void => {
    cancelHoverClose();
    hoverCloseTimer = setTimeout(() => {
      if (pinnedSection) setFlyout(pinnedSection, pinnedAnchor ?? undefined);
      else               setFlyout(null);
    }, 120);
  };

  // Keep flyout open while the cursor is inside it
  flyout.addEventListener('mouseenter', cancelHoverClose);
  flyout.addEventListener('mouseleave', scheduleHoverClose);

  // ── Strip view ────────────────────────────────────────────────────────────

  // Helper: wire up a strip section button (click-pin + hover-preview)
  const addSectionBtn = (btn: HTMLButtonElement, section: FlyoutSection): void => {
    btn.dataset.section = section;
    btn.addEventListener('click', () => {
      if (pinnedSection === section) {
        pinnedSection = null;
        pinnedAnchor  = null;
        syncPinnedHighlight();
        setFlyout(null);
      } else {
        pinnedSection = section;
        pinnedAnchor  = btn;
        syncPinnedHighlight();
        setFlyout(section, btn);
      }
    });
    btn.addEventListener('mouseenter', () => { cancelHoverClose(); setFlyout(section, btn); });
    btn.addEventListener('mouseleave', scheduleHoverClose);
    stripView.appendChild(btn);
  };

  // ── Colour button: strip icon is a live globe reflecting the active colour ─
  const colourGlobe = document.createElement('div');
  colourGlobe.className = 'cfg-colour-strip-globe';
  const initColour = getState().activeConfigurator === 'furniture'
    ? getState().furnitureColor
    : getState().currentColor;
  colourGlobe.style.background = globeBackground(initColour);

  const colourStripBtn = document.createElement('button');
  colourStripBtn.className = 'cfg-strip-btn';
  colourStripBtn.setAttribute('aria-label', 'Colour');
  colourStripBtn.title = 'Colour';
  colourStripBtn.appendChild(colourGlobe);
  colourStripBtn.insertAdjacentHTML('beforeend', `<span class="cfg-strip-btn__label">Colour</span>`);
  addSectionBtn(colourStripBtn, 'colour');

  // ── Variants button ────────────────────────────────────────────────────────
  const variantsStripBtn = makeStripBtn(ICO_VARIANTS, 'Variants');
  addSectionBtn(variantsStripBtn, 'variants');

  const stripDivider = document.createElement('div');
  stripDivider.className = 'cfg-strip-divider';
  stripView.appendChild(stripDivider);

  const resetStripBtn = makeStripBtn(ICO_RESET, 'Reset');
  resetStripBtn.id = 'cfg-reset-camera-strip';
  stripView.appendChild(resetStripBtn);

  const fsStripBtn = makeStripBtn(ICO_FULLSCREEN, 'Fullscreen');
  fsStripBtn.id = 'cfg-fullscreen-strip';
  stripView.appendChild(fsStripBtn);

  const shareStripBtn = makeStripBtn(ICO_SHARE, 'Share');
  shareStripBtn.id = 'cfg-icon-share-strip';
  stripView.appendChild(shareStripBtn);

  const infoStripBtn = makeStripBtn(ICO_INFO, 'Info');
  infoStripBtn.id = 'cfg-icon-info-strip';
  stripView.appendChild(infoStripBtn);

  // ── Full view (rendered once on first expand) ─────────────────────────────
  const colorSlot   = document.createElement('div');
  const variantSlot = document.createElement('div');
  let fullViewBuilt = false;

  const buildFullView = (): void => {
    if (fullViewBuilt) return;
    fullViewBuilt = true;

    const header = document.createElement('div');
    header.className = 'cfg-panel-header';
    const hTitle = document.createElement('span');
    hTitle.className = 'cfg-panel-header__title';
    hTitle.textContent = 'Customization';
    const hDot = document.createElement('div');
    hDot.className = 'cfg-panel-header__dot';
    header.appendChild(hTitle);
    header.appendChild(hDot);

    const inner = document.createElement('div');
    inner.className = 'cfg-panel-content-inner';
    inner.appendChild(colorSlot);
    inner.appendChild(variantSlot);
    inner.appendChild(buildDivider());
    inner.appendChild(buildResetButton());
    inner.appendChild(buildFullscreenButton());
    inner.appendChild(buildIconRow());

    fullView.appendChild(header);
    fullView.appendChild(inner);
    refreshFullSections(getState().activeConfigurator);
  };

  const refreshFullSections = (type: ConfiguratorType): void => {
    const mod = configurators[type];
    const initColor   = type === 'furniture' ? getState().furnitureColor : getState().currentColor;
    const defaultVId  = (mod.variantOptions || DEFAULT_VARIANT_OPTIONS)[0].id;
    const initVariant = type === 'furniture' ? getState().furnitureVariant : defaultVId;
    colorSlot.innerHTML   = '';
    variantSlot.innerHTML = '';
    colorSlot.appendChild(buildColorSection(mod, initColor));
    variantSlot.appendChild(buildVariantSection(mod, initVariant, onVariantModelSwap));
  };

  // Build full view lazily on first expand; single click listener on toggleBtn
  toggleBtn.addEventListener('click', () => {
    const next = !expanded;
    if (next) buildFullView();
    setExpanded(next);
  });

  // ── Flyout renderer ───────────────────────────────────────────────────────
  const renderFlyout = (section: FlyoutSection): void => {
    flyout.innerHTML = '';
    const type = getState().activeConfigurator;
    const mod  = configurators[type];

    if (section === 'colour') {
      const initColor = type === 'furniture' ? getState().furnitureColor : getState().currentColor;
      flyout.appendChild(buildColorGrid(mod, initColor));
    } else if (section === 'variants') {
      const defaultVId  = (mod.variantOptions || DEFAULT_VARIANT_OPTIONS)[0].id;
      const initVariant = type === 'furniture' ? getState().furnitureVariant : defaultVId;
      flyout.appendChild(buildVariantList(mod, initVariant, onVariantModelSwap));
    }
  };

  // ── Sync on configurator / colour switch ──────────────────────────────────
  subscribe((state) => {
    // Always keep the strip globe in sync with the active colour
    const activeHex = state.activeConfigurator === 'furniture'
      ? state.furnitureColor
      : state.currentColor;
    colourGlobe.style.background = globeBackground(activeHex);

    if (expanded) refreshFullSections(state.activeConfigurator);
    else if (activeFlyout) renderFlyout(activeFlyout);
  });

  // Init collapsed
  setExpanded(false);

  // Sync --strip-h on init and resize so tool-row offset stays accurate
  requestAnimationFrame(syncStripH);
  window.addEventListener('resize', syncStripH);

  return wrap;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeStripBtn(icon: string, label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'cfg-strip-btn';
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML = `${icon}<span class="cfg-strip-btn__label">${label}</span>`;
  return btn;
}

// ─── Content builders ─────────────────────────────────────────────────────────

function buildColorGrid(module: ConfiguratorModule, initialHex: string): HTMLElement {
  const grid = document.createElement('div');
  grid.className = 'cfg-color-grid';
  (module.colorOptions || DEFAULT_COLOR_OPTIONS).forEach(({ label, hex }) => {
    const swatch = document.createElement('button');
    swatch.className = 'cfg-color-swatch';
    swatch.title = label;
    swatch.setAttribute('aria-label', label);
    swatch.style.background = globeBackground(hex);
    swatch.dataset.hex = hex;
    if (hex === initialHex) swatch.classList.add('cfg-color-swatch--active');
    swatch.addEventListener('click', () => {
      const { currentModel } = getState();
      if (module.applyColor && currentModel)  { module.applyColor(currentModel, hex); setState({ furnitureColor: hex }); }
      else if (currentModel)                  { applyColorToModel(currentModel, hex); setState({ currentColor: hex }); }
      grid.querySelectorAll<HTMLElement>('.cfg-color-swatch').forEach((s) => s.classList.remove('cfg-color-swatch--active'));
      swatch.classList.add('cfg-color-swatch--active');
    });
    grid.appendChild(swatch);
  });
  return grid;
}

function buildVariantList(
  module: ConfiguratorModule,
  initialVariantId: string,
  onVariantModelSwap?: () => void
): HTMLElement {
  const list = document.createElement('div');
  list.className = 'cfg-variant-list';
  (module.variantOptions || DEFAULT_VARIANT_OPTIONS).forEach(({ id, label }, idx) => {
    const btn = document.createElement('button');
    btn.className = 'cfg-variant-btn';
    const iconSvg = VARIANT_ICONS[idx] ?? VARIANT_ICONS[VARIANT_ICONS.length - 1];
    btn.innerHTML = `<span class="cfg-variant-btn__icon">${iconSvg}</span><span class="cfg-variant-btn__label">${label}</span>`;
    if (id === initialVariantId) btn.classList.add('cfg-variant-btn--active');
    btn.addEventListener('click', () => {
      list.querySelectorAll<HTMLElement>('.cfg-variant-btn').forEach((b) => b.classList.remove('cfg-variant-btn--active'));
      btn.classList.add('cfg-variant-btn--active');
      setState({ furnitureVariant: id });
      if (module.variantModelPaths?.[id] && onVariantModelSwap) {
        // Variant has its own GLB — trigger a full model reload
        onVariantModelSwap();
      } else {
        const { currentModel } = getState();
        if (module.applyVariant && currentModel) module.applyVariant(currentModel, id);
        else console.log(`[Configurator] Variant → ${id}`);
      }
    });
    list.appendChild(btn);
  });
  return list;
}

function buildColorSection(module: ConfiguratorModule, initialHex: string): HTMLElement {
  const s = document.createElement('div');
  const l = document.createElement('p'); l.className = 'cfg-section-label';
  l.textContent = module.colorOptions ? 'Leather Colour' : 'Colour';
  s.appendChild(l); s.appendChild(buildColorGrid(module, initialHex));
  return s;
}

function buildVariantSection(
  module: ConfiguratorModule,
  initialVariantId: string,
  onVariantModelSwap?: () => void
): HTMLElement {
  const s = document.createElement('div');
  const l = document.createElement('p'); l.className = 'cfg-section-label';
  l.textContent = 'Variants';
  s.appendChild(l); s.appendChild(buildVariantList(module, initialVariantId, onVariantModelSwap));
  return s;
}

function buildDivider(): HTMLElement {
  const d = document.createElement('div'); d.className = 'cfg-divider'; return d;
}

function buildResetButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'cfg-reset-btn'; btn.id = 'cfg-reset-camera';
  btn.innerHTML = `${ICO_RESET} Reset Camera`; return btn;
}

function buildFullscreenButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'cfg-fullscreen-btn'; btn.id = 'cfg-fullscreen-btn';
  btn.innerHTML = `${ICO_FULLSCREEN} Enter Fullscreen`;
  btn.setAttribute('aria-label', 'Toggle fullscreen'); return btn;
}

function buildIconRow(): HTMLElement {
  const row = document.createElement('div'); row.className = 'cfg-icon-row';
  const share = document.createElement('button');
  share.className = 'cfg-icon-btn'; share.id = 'cfg-icon-share';
  share.setAttribute('aria-label', 'Share'); share.innerHTML = ICO_SHARE;
  const info = document.createElement('button');
  info.className = 'cfg-icon-btn'; info.id = 'cfg-icon-info';
  info.setAttribute('aria-label', 'Info'); info.innerHTML = ICO_INFO;
  row.appendChild(share); row.appendChild(info); return row;
}
